import React, { useState } from 'react'
import styled from 'styled-components'
import { useStaticQuery, graphql, Link } from 'gatsby'

// Elements
import Plaatjie from '@ubo/plaatjie'

// Images
import Logo from 'img/logo.inline.svg'
import Phone from 'img/icon/phone.inline.svg'
import menuBackgroundImage from 'img/menu.jpg'
import Demo from '../elements/Demo'

const StyledHeader = styled.nav`
  position: fixed;
  left: 0;
  top: 0;
  right: 0;
  z-index: 15;
  box-shadow: 0 0 8px rgba(0,0,0,.25);
`

const Top = styled.div``

const Bottom = styled.div`
  background: ${props => props.theme.color.face.main};
  background: ${props => `linear-gradient(90deg, rgba(${props.theme.color.gradient.main.from},1) 0%, rgba(${props.theme.color.gradient.main.to},1) 95%)`};
  
  @media (min-width: 576px) {
    height: 70px;
  }

  @media (max-width: 576px) {
    height: 55px;
  }
`

const Container = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const Brand = styled(Link)`
  & > svg {
    @media (min-width: 576px) {
      height: 70px;
      width: 285px;
    }

    @media (max-width: 576px) {
      height: 55px;
      width: 225px;
    }
  }
`

const NavButton = styled.button`
  width: 80px;
  height: 50px;
  padding: 0 10px;
  text-align: center;
  z-index: 10;

  @media (min-width: 576px) {
    height: 50px;
  }

  @media (max-width: 576px) {
    height: 45px;
  }
`

const NavButtonTitle = styled.div`
  color: ${props => props.theme.color.text.light};
  text-transform: uppercase;

  @media (min-width: 576px) {
    font-size: ${props => props.theme.font.size.m};
  }

  @media (max-width: 576px) {
    font-size: ${props => props.theme.font.size.sm};
  }
`

const NavButtonStripe = styled.div`
  background-color: ${props => props.theme.color.text.light};
  height: 3px;
  width: 50%;
  
  @media (min-width: 576px) {
    margin: 5px auto;
  }

  @media (max-width: 576px) {
    margin: 3px auto;
  }
`

const PhoneButton = styled.a`
  color: ${props => props.theme.color.text.main};
`

const PhoneIcon = styled(Phone)`
  margin-right: 10px;
  height: 20px;
  width: 20px;
  position: relative;
  bottom: 2px;
`

const BenefitIcon = styled(Plaatjie)`
  width: 18px;
  height: 18px;
  display: block;
  position: relative;
  top: 3px;
`

const Header = ({ isSticky, setIsScrollable }) => {
  const {
    fields,
  } = useStaticQuery(graphql`
    {
      fields: wpComponent(id: {eq: "cG9zdDo5OA=="}) {
        header {
          top {
            phonenumber
            benefits {
              icon {
                localFile {
                  publicURL
                }
              }
              title
            }
          }
          bottom {
            menu {
              link {
                title
                url
              }
            }
            cta {
              title
              url
            }
          }
        }
      }
    }
  `)

  return (
    <StyledHeader isSticky={isSticky}>
      <Top className="color-background-light">
        <div className="container d-flex justify-content-between align-items-center py-2">
          <div className="d-lg-flex align-items-center">
            {fields.header.top.benefits.map((edge, index) => (
              // eslint-disable-next-line
              <div key={index} className="me-5 d-none d-lg-flex">
                <BenefitIcon image={edge.icon} alt={edge.title} />
                <div className="ms-2 font-size-14 font-weight-l">
                  {edge.title}
                </div>
              </div>
            ))}
          </div>

          <PhoneButton href={`tel:${fields.header.top.phonenumber}`} className="font-weight-l">
            <PhoneIcon />
            {fields.header.top.phonenumber}
          </PhoneButton>
        </div>
      </Top>
      <Bottom>
        <Container className="container h-100">
          <Brand to="/">
            <Logo />
          </Brand>

          <HeaderMenuMobile fields={fields} setIsScrollable={setIsScrollable} />
          <HeaderMenuDesktop fields={fields} />
        </Container>
      </Bottom>
    </StyledHeader>
  )
}

const MenuWrapper = styled.div`
  background-color: ${props => props.theme.color.face.main};
  position: fixed;
  left: 0;
  top: 0;
  width: 100vw;
  height: 100vh;
  z-index: 99;
  padding: 30px 0 0;

  & ul {
    padding: 50px 0 0;
    list-style-type: none;
    text-align: center;
    width: 60%;
    margin: 0 auto;

    & li {
      padding: 0;

      & a {
        font-size: ${props => props.theme.font.size.xm};
        border-bottom: 1px solid #f1f1f1;
        display: block;
        padding: 10px 0;
      }

      &:last-child a {
        border-bottom: none;
      }
    }
  }
`

const MenuBackground = styled(Plaatjie)`
  position: absolute !important;
  left: 0;
  top: 0;
  bottom: 0;
  right: 0;
  z-index: 1;
`

const MenuBackgroundOverlay = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  right: 0;
  background: ${props => `rgba(${props.theme.color.gradient.main.from},.5)`};
  background: ${props => `radial-gradient(circle, rgba(${props.theme.color.gradient.main.from},.65) 0%, rgba(${props.theme.color.gradient.main.to},.8) 60%)`};
  z-index: 2;
`

const MenuBackgroundContent = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  right: 0;
  z-index: 3;
`

const NavButtonClose = styled.div`
  position: absolute;
  right: 20px;
  top: 20px;
  text-align: center;
`

const NavButtonCloseCross = styled.div`
  position: relative;
  width: 32px;
  height: 32px;
  margin-left: 16px;

  &:before, &:after {
    background-color: ${props => props.theme.color.text.light};
    position: absolute;
    content: ' ';
    height: 33px;
    width: 2px;
  }

  &:before {
    transform: rotate(45deg);
  }

  &:after {
    transform: rotate(-45deg);
  }
`

const NavButtonCloseTitle = styled.div`
  color: ${props => props.theme.color.text.light};
  text-transform: uppercase;
  margin-top: 3px;
`

const HeaderMenuMobile = ({ fields, setIsScrollable }) => {
  const [isOpen, setIsOpen] = useState(false)

  return (
    <div className="d-block d-lg-none">
      <NavButton
        role="button"
        onClick={() => {
          setIsOpen(!isOpen)
          setIsScrollable(false)
        }}
      >
        <NavButtonStripe />
        <NavButtonStripe />
        <NavButtonStripe />
        <NavButtonTitle>
          Menu
        </NavButtonTitle>
      </NavButton>
      {isOpen && (
        <MenuWrapper>
          <MenuBackground image={{ url: menuBackgroundImage }} alt="123zakelijkinternet" />
          <MenuBackgroundOverlay />
          <MenuBackgroundContent>
            <NavButtonClose
              role="button"
              onClick={() => {
                setIsOpen(!isOpen)
                setIsScrollable(true)
              }}
            >
              <NavButtonCloseCross />
              <NavButtonCloseTitle>
                Sluiten
              </NavButtonCloseTitle>
            </NavButtonClose>
            <ul className="mt-5">
              <HeaderMenuItems fields={fields} />
            </ul>
          </MenuBackgroundContent>
        </MenuWrapper>
      )}
    </div>
  )
}

const StyledHeaderMenuDesktop = styled.ul`
  flex: 1;
  align-items: center;
  justify-content: space-around;
  list-style: none;
  margin-bottom: 0;
`

const HeaderMenuDesktop = ({ fields }) => (
  <div className="w-100 ps-lg-5 ms-lg-5 d-none d-lg-block">
    <StyledHeaderMenuDesktop className="d-none d-lg-flex ps-lg-5 ms-lg-5">
      <HeaderMenuItems fields={fields} />
    </StyledHeaderMenuDesktop>
  </div>
)

const Item = styled.li`
  & > a, & > button {
    color: ${props => props.theme.color.text.light};

    &[aria-current] {
      font-weight: ${props => props.theme.font.weight.l};
      text-decoration: underline;
    }

    &:hover {
      color: ${props => props.theme.color.text.light};
      text-decoration: underline;
    }
  }
`

const C2A = styled(Link)`
  font-weight: ${props => props.theme.font.weight.s} !important;
  border-radius: 15px;
  padding: 5px 20px !important;
  text-decoration: none !important;

  &:hover {
    color: ${props => props.theme.color.text.light};
  }
`

const HeaderMenuItems = ({ fields }) => {
  const [showDemo, setShowDemo] = React.useState(false)

  return (
    <>
      {fields.header.bottom.menu.map(({ link: { url, title } }, index) => (
        // eslint-disable-next-line
        <Item key={index}>
          <Link to={url}>{title}</Link>
        </Item>
      ))}
      <Item>
        <button type="button" onClick={() => { setShowDemo(true) }}>demo</button>
      </Item>
      <Item>
        <C2A to={fields.header.bottom.cta.url} className="color-background-contrast color-light mt-5 mt-lg-0">
          {fields.header.bottom.cta.title}
        </C2A>
      </Item>

      {showDemo && (
        <Demo 
          onClose={() => { setShowDemo(false) }} 
          title="Demo aanvragen"
          description="Plan hieronder een afspraak. Dan bekijken wij samen de mogelijkheden voor jouw bedrijf."
        />
      )}
    </>
  )
}

export default Header
